/* eslint-disable react/no-unknown-property */
import {
  ActionIcon,
  Card,
  FontWeight,
  HStack,
  ICON_EYE_OUTLINE,
  ICON_FILE_DOCUMENT_OUTLINE,
  ModalSize,
  Portal,
  Stack,
  Text,
  useModal,
  SimpleTable,
} from '@doveit/bricks';
import React from 'react';
import { DOCUMENTATION_STORAGE_BASE_URL } from '../../../resources/pages/view/constants';
import * as styles from './CataloguePage.style';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { ThemeIcons } from '../../../theme/icons';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';
import { useCurrentAgentSync } from '../../../hooks/use-agent/useAgent';
import { getActualMarketingResources } from './utils';
import { FileMimeTypeExtensions } from '../../../constants';
import { MarketingResource } from './constants';
import { FileMimeType } from '../../../types';
import useRBAC from '../../../hooks/use-rbac/useRBAC';

const CataloguePage: React.FC = () => {
  const { data: currentAgent } = useCurrentAgentSync();
  const isMobile = useIsDevice('mobile');
  const { userIsAdmin, userIsMarketing } = useRBAC();
  const pdfPreviewModal = useModal<MarketingResource>();

  const onPreviewOpenModal = React.useCallback((resource: MarketingResource) => () => pdfPreviewModal.open(resource), [pdfPreviewModal]);
  const marketingResources = React.useMemo(() => getActualMarketingResources(currentAgent, userIsAdmin || userIsMarketing), [currentAgent, userIsAdmin, userIsMarketing]);

  const isPreviewable = React.useCallback((resource: MarketingResource) => resource.format === FileMimeType.PDF, []);

  return (
    <>
      <SimplePageLayout>
        <Card paddingSize="M">
          <Card.Header
            icon={{ path: ICON_FILE_DOCUMENT_OUTLINE }}
            title="Materiali promozionali"
          />
          <Card.Content>
            <Text.Body>
              In questa sezione troverai il catalogo dei materiali promozionali di Dove.it e le istruzioni per ordinarli.
            </Text.Body>
          </Card.Content>
          <Card.Content>
            {!isMobile && (
              <SimpleTable>
                <SimpleTable.Body>
                  {marketingResources.map((resource) => (
                    <SimpleTable.Row key={resource.title}>

                      <SimpleTable.Cell>
                        <Text.Body fontWeight={FontWeight.REGULAR}>
                          {resource.title}
                        </Text.Body>
                      </SimpleTable.Cell>

                      <SimpleTable.Cell>
                        <Text.Body>
                          {resource.description}
                        </Text.Body>
                      </SimpleTable.Cell>

                      <SimpleTable.Cell align="right">
                        <HStack wrap="nowrap">
                          {isPreviewable(resource) && (
                            <ActionIcon
                              aria-label={`Anteprima ${resource.title}`}
                              label={`Anteprima ${resource.title}`}
                              size="S"
                              onClick={onPreviewOpenModal(resource)}
                              icon={{ path: ICON_EYE_OUTLINE }}
                            />
                          )}

                          <ActionIcon
                            aria-label={`Scarica ${resource.title}`}
                            label={`Scarica ${resource.title}`}
                            size="S"
                            icon={{ path: ThemeIcons.DOWNLOAD }}
                            href={`${DOCUMENTATION_STORAGE_BASE_URL}${resource.fileName}${FileMimeTypeExtensions[resource.format]}`}
                            download
                            target="_blank"
                          />
                        </HStack>
                      </SimpleTable.Cell>
                    </SimpleTable.Row>
                  ))}
                </SimpleTable.Body>
              </SimpleTable>
            )}

            {isMobile && (
              <Stack gap={150}>
                {marketingResources.map((resource) => (
                  <Card key={resource.title}>
                    <Card.Header
                      title={resource.title}
                      primaryActions={[
                        <ActionIcon
                          aria-label={`Scarica ${resource.title}`}
                          label={`Scarica ${resource.title}`}
                          size="S"
                          icon={{ path: ThemeIcons.DOWNLOAD }}
                          href={`${DOCUMENTATION_STORAGE_BASE_URL}${resource.fileName}${FileMimeTypeExtensions[resource.format]}`}
                          download
                          target="_blank"
                        />,
                      ]}
                    />
                    <Card.Content>
                      <Text.Body>
                        {resource.description}
                      </Text.Body>
                    </Card.Content>
                  </Card>
                ))}
              </Stack>
            )}
          </Card.Content>
        </Card>
      </SimplePageLayout>
      {pdfPreviewModal.data && (
        <Portal>
          <SimpleModal
            {...pdfPreviewModal}
            size={ModalSize.MEDIUM}
            title={pdfPreviewModal.data?.title}
            padded={false}
            fillAvailableHeight
          >
            <div
              css={styles.previewWrapper}
              height="100%"
            >
              <iframe
                src={`${DOCUMENTATION_STORAGE_BASE_URL}${pdfPreviewModal.data.fileName}${FileMimeTypeExtensions[pdfPreviewModal.data.format]}`}
                title={pdfPreviewModal.data.fileName}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            </div>
          </SimpleModal>
        </Portal>
      )}
    </>
  );
};

export default CataloguePage;
